import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Lock, LogOut, RefreshCcw, Search, CheckCircle2, XCircle, AlertTriangle, Mail } from 'lucide-react';
import './Auth.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'pgsrh_admin_token';
const TOKEN_EXP_KEY = 'pgsrh_admin_token_exp';

const Auth = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || '');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [apps, setApps] = useState([]);
  const [loadingApps, setLoadingApps] = useState(false);
  const [refreshError, setRefreshError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_EXP_KEY);
    setToken('');
    setApps([]);
    setPassword('');
  }, []);

  const fetchApplications = useCallback(async (bearer) => {
    setLoadingApps(true);
    setRefreshError('');
    try {
      const { data } = await axios.get(`${API}/auth/applications?limit=1000`, {
        headers: { Authorization: `Bearer ${bearer}` },
        timeout: 15000,
      });
      setApps(data.applications || []);
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
        setLoginError('Your session expired. Please sign in again.');
      } else {
        setRefreshError(err.response?.data?.detail || 'Could not load applications.');
      }
    } finally {
      setLoadingApps(false);
    }
  }, [logout]);

  // Auto-fetch on mount if we have a token
  useEffect(() => {
    if (token) fetchApplications(token);
  }, [token, fetchApplications]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoggingIn(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { password },
        { headers: { 'Content-Type': 'application/json' }, timeout: 15000 }
      );
      sessionStorage.setItem(TOKEN_KEY, data.token);
      if (data.expiresAt) sessionStorage.setItem(TOKEN_EXP_KEY, data.expiresAt);
      setToken(data.token);
      setPassword('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setLoginError(typeof detail === 'string' ? detail : 'Login failed. Please try again.');
    } finally {
      setLoggingIn(false);
    }
  };

  // ---------- Login screen ----------
  if (!token) {
    return (
      <div className="auth-shell">
        <div className="auth-card" data-testid="auth-login-card">
          <div className="auth-lock-badge" aria-hidden="true">
            <Lock size={22} />
          </div>
          <h1 className="auth-title">Admin Access</h1>
          <p className="auth-subtitle">Sign in to review submitted applications.</p>
          <form onSubmit={handleLogin} className="auth-form" noValidate>
            <label htmlFor="admin-pw" className="auth-label">Password</label>
            <input
              id="admin-pw"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              autoComplete="current-password"
              autoFocus
              className={`auth-input ${loginError ? 'auth-input-invalid' : ''}`}
              data-testid="auth-password-input"
            />
            {loginError && (
              <p className="auth-error" role="alert" data-testid="auth-error">
                <AlertTriangle size={16} /> {loginError}
              </p>
            )}
            <button
              type="submit"
              className="auth-submit"
              disabled={loggingIn || !password}
              data-testid="auth-submit-button"
            >
              {loggingIn ? 'Verifying…' : 'Sign In'}
            </button>
          </form>
          <p className="auth-hint">This page is not linked from the public site.</p>
        </div>
      </div>
    );
  }

  // ---------- Dashboard ----------
  const filtered = apps.filter((a) => {
    if (statusFilter !== 'all' && (a.emailStatus || 'unknown') !== statusFilter) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (a.firstName || '').toLowerCase().includes(q) ||
      (a.lastName || '').toLowerCase().includes(q) ||
      (a.email || '').toLowerCase().includes(q) ||
      (a.phone || '').toLowerCase().includes(q) ||
      (a.county || '').toLowerCase().includes(q) ||
      (a.facilityName || '').toLowerCase().includes(q) ||
      (a.id || '').toLowerCase().includes(q)
    );
  });

  const statusCounts = apps.reduce((acc, a) => {
    const s = a.emailStatus || 'unknown';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const selected = selectedId ? apps.find((a) => a.id === selectedId) : null;

  const renderStatusPill = (status) => {
    const map = {
      sent: { icon: <CheckCircle2 size={12} />, cls: 'pill-sent', label: 'Email sent' },
      failed: { icon: <XCircle size={12} />, cls: 'pill-failed', label: 'Email failed' },
      pending: { icon: <Mail size={12} />, cls: 'pill-pending', label: 'Pending' },
    };
    const cfg = map[status] || { icon: <Mail size={12} />, cls: 'pill-unknown', label: status || 'unknown' };
    return <span className={`status-pill ${cfg.cls}`}>{cfg.icon} {cfg.label}</span>;
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
    } catch { return iso; }
  };

  return (
    <div className="auth-shell dashboard">
      <header className="dash-header">
        <div className="dash-header-left">
          <h1 className="dash-title">Applications Archive</h1>
          <p className="dash-subtitle">{apps.length} total submission{apps.length === 1 ? '' : 's'}</p>
        </div>
        <div className="dash-header-actions">
          <button className="dash-btn ghost" onClick={() => fetchApplications(token)} disabled={loadingApps} data-testid="refresh-button">
            <RefreshCcw size={16} className={loadingApps ? 'spin' : ''} />
            <span>Refresh</span>
          </button>
          <button className="dash-btn" onClick={logout} data-testid="logout-button">
            <LogOut size={16} />
            <span>Sign out</span>
          </button>
        </div>
      </header>

      <div className="dash-toolbar">
        <div className="dash-search-wrap">
          <Search size={16} className="dash-search-icon" />
          <input
            type="text"
            className="dash-search"
            placeholder="Search by name, email, phone, county, facility, or ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="dash-search"
          />
        </div>
        <div className="dash-filter-chips">
          {[
            { key: 'all', label: `All (${apps.length})` },
            { key: 'sent', label: `Sent (${statusCounts.sent || 0})` },
            { key: 'failed', label: `Failed (${statusCounts.failed || 0})` },
            { key: 'pending', label: `Pending (${statusCounts.pending || 0})` },
            { key: 'unknown', label: `Legacy (${statusCounts.unknown || 0})` },
          ].map((f) => (
            <button
              key={f.key}
              className={`chip ${statusFilter === f.key ? 'chip-active' : ''}`}
              onClick={() => setStatusFilter(f.key)}
              data-testid={`chip-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {refreshError && (
        <div className="dash-banner" role="alert">
          <AlertTriangle size={16} /> {refreshError}
        </div>
      )}

      <div className="dash-body">
        <div className="dash-table-wrap">
          <table className="dash-table">
            <thead>
              <tr>
                <th>Submitted</th>
                <th>Name</th>
                <th>Email / Phone</th>
                <th>County</th>
                <th>SCA</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && !loadingApps && (
                <tr><td colSpan="6" className="dash-empty">No applications match this filter.</td></tr>
              )}
              {filtered.map((a) => (
                <tr
                  key={a.id}
                  className={`dash-row ${selectedId === a.id ? 'dash-row-active' : ''}`}
                  onClick={() => setSelectedId(a.id)}
                  data-testid={`app-row-${a.id}`}
                >
                  <td className="mono">{fmtDate(a.submittedAt)}</td>
                  <td><strong>{a.firstName} {a.lastName}</strong></td>
                  <td>
                    <div className="cell-multi">
                      <span>{a.email || '—'}</span>
                      <span className="muted">{a.phone || '—'}</span>
                    </div>
                  </td>
                  <td>{a.county || '—'}</td>
                  <td>
                    {a.scaFunding
                      ? <span className={`sca-pill ${a.scaFunding === 'yes' ? 'sca-yes' : 'sca-no'}`}>{a.scaFunding.toUpperCase()}</span>
                      : <span className="muted">—</span>}
                  </td>
                  <td>{renderStatusPill(a.emailStatus)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {selected && (
          <aside className="dash-detail" data-testid="app-detail">
            <div className="dash-detail-header">
              <h2>{selected.firstName} {selected.lastName}</h2>
              <button className="dash-detail-close" onClick={() => setSelectedId(null)} aria-label="Close details">×</button>
            </div>
            <div className="dash-detail-meta">
              <span className="mono">ID: {selected.id}</span>
              <span>Submitted: {fmtDate(selected.submittedAt)}</span>
              {renderStatusPill(selected.emailStatus)}
            </div>
            <DetailSection title="Personal">
              <DetailRow label="Date of birth" value={selected.birthday} />
              <DetailRow label="Ethnicity" value={selected.ethnicity} />
              <DetailRow label="Sex" value={selected.sex} />
              <DetailRow label="Identifies as" value={selected.identifiedAs} />
              <DetailRow label="Phone" value={selected.phone} />
              <DetailRow label="Email" value={selected.email} />
              <DetailRow label="Home address" value={selected.homeAddress} />
              <DetailRow label="County" value={selected.county} />
            </DetailSection>
            <DetailSection title="Referral">
              <DetailRow label="Facility / program" value={selected.facilityName} />
              <DetailRow label="Case manager phone" value={selected.caseManagerPhone} />
              <DetailRow label="Extension" value={selected.caseManagerExtension} />
              <DetailRow label="Case manager email" value={selected.caseManagerEmail} />
            </DetailSection>
            <DetailSection title="Payment & documents">
              <DetailRow label="Payment source" value={selected.paymentSource} />
              <DetailRow label="Documents" value={(selected.documents || []).join(', ')} />
            </DetailSection>
            <DetailSection title="Medical & treatment">
              <DetailRow label="Primary doctor" value={selected.primaryDoctor} />
              <DetailRow label="Doctor phone" value={selected.primaryDoctorPhone} />
              <DetailRow label="Discharge date" value={selected.dischargeDate} />
              <DetailRow label="Times in treatment" value={selected.treatmentTimes} />
            </DetailSection>
            <DetailSection title="Program history & funding">
              <DetailRow label="Previous ProSolve resident" value={selected.previousResident} />
              <DetailRow label="Applying for Lehigh SCA funding" value={selected.scaFunding} />
            </DetailSection>
            <DetailSection title="Email delivery">
              <DetailRow label="Status" value={selected.emailStatus} />
              <DetailRow label="Attempts" value={selected.emailAttempts != null ? String(selected.emailAttempts) : null} />
              <DetailRow label="Sent at" value={selected.emailSentAt ? fmtDate(selected.emailSentAt) : null} />
              <DetailRow label="Recipients" value={selected.emailRecipientCount != null ? String(selected.emailRecipientCount) : null} />
              <DetailRow label="BCC delivered to" value={selected.emailBccCount != null ? String(selected.emailBccCount) : null} />
              {selected.emailError && <DetailRow label="Last error" value={selected.emailError} error />}
            </DetailSection>
          </aside>
        )}
      </div>
    </div>
  );
};

const DetailSection = ({ title, children }) => (
  <section className="detail-section">
    <h3>{title}</h3>
    <dl>{children}</dl>
  </section>
);

const DetailRow = ({ label, value, error }) => (
  <>
    <dt>{label}</dt>
    <dd className={error ? 'detail-error' : ''}>{value || <span className="muted">—</span>}</dd>
  </>
);

export default Auth;
